import { useState, useEffect, useCallback, useRef } from "react";
import type {
  ChildTableRow,
  UseChildTableProps,
  UseChildTableReturn,
} from "./types";

/**
 * Generate a unique local ID for new rows
 */
function generateLocalId(): string {
  return `__local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Initialize a row with state tracking
 */
function initRow(row: ChildTableRow): ChildTableRow {
  const isNew = row.__islocal === 1 || !row.name;
  return {
    ...row,
    __state: isNew ? 'new' : 'unchanged',
    __originalData: { ...row }, // Deep clone for comparison
    __deleted: false,
    _localId: isNew && !row._localId ? generateLocalId() : row._localId,
  };
}

/**
 * Sanitize row data - remove UI-only fields before passing to parent
 */
function sanitizeRow(row: ChildTableRow): Omit<ChildTableRow, '__state' | '__originalData' | '__deleted' | '_localId'> {
  const { __state, __originalData, __deleted, _localId, ...cleanRow } = row;
  return cleanRow;
}

/**
 * Hook that manages child table array state and operations
 * 
 * Uses resetKey pattern for explicit resync control:
 * - Resets rows when resetKey changes (e.g., after save when new rows get real names)
 * - After reset, hook owns state and ignores further initialRows changes
 * 
 * CONTRACT: onChange is NEVER called during render. All onChange calls are deferred
 * via pendingOnChangeRef + useEffect to prevent "setState in render" errors.
 */
export function useChildTable({
  initialRows,
  childFields,
  onChange,
  resetKey,
}: UseChildTableProps): UseChildTableReturn {
  // NEW: track whether we've already hydrated from non-empty initialRows
  const hasHydratedFromInitialRef = useRef(false);

  // Initialize rows with state tracking
  // Update initializer to set hydration flag if we start with data
  const [rows, setRows] = useState<Array<ChildTableRow>>(() => {
    const base = (initialRows || []).map(initRow);
    if (base.length > 0) {
      hasHydratedFromInitialRef.current = true;
    }
    return base;
  });

  // Use ref to track pending onChange calls - prevents setState during render
  const pendingOnChangeRef = useRef<Array<ChildTableRow> | null>(null);

  // 🔒 Effect: ONLY place where onChange is invoked (after state update completes)
  useEffect(() => {
    if (!onChange) {
      // Guard: if onChange is not provided, clear pending and return
      pendingOnChangeRef.current = null;
      return;
    }

    if (pendingOnChangeRef.current !== null) {
      const rawRows = pendingOnChangeRef.current;
      
      // Sanitize before sending up - remove all UI-only fields
      const cleanRows = rawRows
        .filter((row) => !row.__deleted) // Filter out deleted rows
        .map(sanitizeRow);
      
      onChange(cleanRows);
      pendingOnChangeRef.current = null;
    }
  }, [rows, onChange]);

  // Reset rows when resetKey changes
  // CONTRACT: Reset does NOT call onChange (pendingOnChangeRef stays null)
  useEffect(() => {
    // Only source of truth is new initialRows from parent.
    // Do NOT set pendingOnChangeRef here - reset is not a user action.
    setRows((initialRows || []).map(initRow));
    hasHydratedFromInitialRef.current = (initialRows?.length || 0) > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // ^ We intentionally only depend on resetKey, not initialRows.
    // initialRows is "server canonical state" that we resync from when resetKey changes.
  }, [resetKey]);

  // NEW: Separate hydration effect for "late data arrival"
  useEffect(() => {
    const incoming = initialRows || [];

    // Case: we mounted with no rows, and later data arrived
    if (!hasHydratedFromInitialRef.current && rows.length === 0 && incoming.length > 0) {
      setRows(incoming.map(initRow));
      hasHydratedFromInitialRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRows, rows.length]);

  // Generate default row (using functional setState for better React concurrent mode support)
  const getDefaultRow = useCallback((currentRows: Array<ChildTableRow>) => {
    const newRow: ChildTableRow = {
      idx: currentRows.length + 1,
      docstatus: 0,
      __islocal: 1,
      __state: 'new',
      __deleted: false,
      _localId: generateLocalId(),
    };

    // Add default values from field metadata
    for (const field of childFields) {
      if (field.default !== undefined) {
        newRow[field.fieldname] = field.default;
      }
    }

    return newRow;
  }, [childFields]);

  // Add new row (using functional setState)
  // CONTRACT: Sets pendingOnChangeRef, does NOT call onChange directly
  const addRow = useCallback(() => {
    setRows((prev) => {
      const newRow = getDefaultRow(prev);
      const nextRows = [...prev, newRow];
      pendingOnChangeRef.current = nextRows; // Schedule onChange for after state update
      return nextRows;
    });
  }, [getDefaultRow]);

  // Delete row - soft delete for existing, hard delete for new (using functional setState)
  // CONTRACT: Sets pendingOnChangeRef, does NOT call onChange directly
  // RULE: This is pure UI logic - only manipulates rows and calls onChange(newRows)
  // No direct Frappe calls - persistence is handled by parent component (MetaFieldEditModal)
  const deleteRow = useCallback(
    (index: number) => {
      setRows((prev) => {
        const row = prev[index];
        
        if (row.__state === 'new') {
          // New row - remove completely
          const nextRows = prev.filter((_, i) => i !== index);
          const recalculatedRows = nextRows.map((r, i) => ({
            ...r,
            idx: i + 1,
          }));
          pendingOnChangeRef.current = recalculatedRows; // Schedule onChange
          return recalculatedRows;
        } else {
          // Existing row - soft delete
          const nextRows = [...prev];
          nextRows[index] = {
            ...row,
            __deleted: true,
            __state: 'deleted',
            docstatus: 2,
          };
          pendingOnChangeRef.current = nextRows; // Schedule onChange
          return nextRows;
        }
      });
    },
    [] // No dependencies - onChange is handled via ref
  );

  // Duplicate row (using functional setState)
  // CONTRACT: Sets pendingOnChangeRef, does NOT call onChange directly
  const duplicateRow = useCallback(
    (index: number) => {
      setRows((prev) => {
        const rowToDuplicate = prev[index];
        const duplicatedRow: ChildTableRow = {
          ...rowToDuplicate,
          idx: prev.length + 1,
          __islocal: 1,
          __state: 'new',
          __deleted: false,
          _localId: generateLocalId(),
          name: undefined, // Force new row
        };
        const nextRows = [...prev];
        nextRows.splice(index + 1, 0, duplicatedRow);
        const recalculatedRows = nextRows.map((row, i) => ({
          ...row,
          idx: i + 1,
        }));
        pendingOnChangeRef.current = recalculatedRows; // Schedule onChange
        return recalculatedRows;
      });
    },
    [] // No dependencies - onChange is handled via ref
  );

  // Update row field (using functional setState)
  // CONTRACT: Sets pendingOnChangeRef, does NOT call onChange directly
  const updateRow = useCallback(
    (index: number, patch: Partial<ChildTableRow>) => {
      setRows((prev) => {
        const currentRow = prev[index];
        
        // Don't update if row is deleted
        if (currentRow.__deleted) {
          return prev;
        }
        
        const updatedRow = { ...currentRow, ...patch };
        
        // Check if row was actually modified
        // Compare against original data (not current state)
        const updatedData = { ...updatedRow };
        delete updatedData.__state;
        delete updatedData.__originalData;
        delete updatedData.__deleted;
        delete updatedData._localId;
        
        const isModified = JSON.stringify(currentRow.__originalData) !== 
                          JSON.stringify(updatedData);
        
        updatedRow.__state = currentRow.__state === 'new' ? 'new' : 
                            (isModified ? 'modified' : 'unchanged');
        
        const nextRows = [...prev];
        nextRows[index] = updatedRow;
        pendingOnChangeRef.current = nextRows; // Schedule onChange
        return nextRows;
      });
    },
    [] // No dependencies - onChange is handled via ref
  );

  return {
    rows,
    addRow,
    deleteRow,
    duplicateRow,
    updateRow,
  };
}

